/**
 * PackerScanConfirmation — Two-step badge + label scan confirmation UI.
 * Extracted from MobilePackerView for readability.
 */
import React from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import {
  QrCode,
  User,
  CheckCircle,
  X,
  Loader2,
  Camera,
} from 'lucide-react';

interface PackerScanConfirmationProps {
  scanStep: 'badge' | 'label';
  scannedBadge: string | null;
  isLoading: boolean;
  manualInput: string;
  setManualInput: (v: string) => void;
  handleManualScanSubmit: () => void;
  handleCancelConfirm: () => void;
  badgeScanInputRef: React.RefObject<HTMLInputElement>;
  labelScanInputRef: React.RefObject<HTMLInputElement>;
  setShowCamera: (v: boolean) => void;
}

const PackerScanConfirmation: React.FC<PackerScanConfirmationProps> = ({
  scanStep,
  scannedBadge,
  isLoading,
  manualInput,
  setManualInput,
  handleManualScanSubmit,
  handleCancelConfirm,
  badgeScanInputRef,
  labelScanInputRef,
  setShowCamera,
}) => {
  const { t } = useLanguage();

  return (
    <div className="flex-1 flex flex-col md:justify-center">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
          {t('mobile.confirmLabel') || 'Confirm Label Applied'}
        </h2>
        <p className="text-gray-500 mt-1">
          {t('mobile.scanToConfirm') || 'Scan badge then label to confirm'}
        </p>
      </div>

      {/* Scan Steps */}
      <div className="space-y-4 mb-6">
        {/* Step 1: Badge */}
        <div className={`p-4 md:p-5 rounded-xl border-2 transition-all ${
          scanStep === 'badge'
            ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
            : scannedBadge
              ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
              : 'border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center ${
              scannedBadge
                ? 'bg-green-100 dark:bg-green-800'
                : scanStep === 'badge'
                  ? 'bg-amber-100 dark:bg-amber-800 animate-pulse'
                  : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              {scannedBadge ? (
                <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
              ) : (
                <User className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              )}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-white">
                {t('mobile.step1ScanBadge') || '1. Scan Your Badge'}
              </p>
              {scannedBadge ? (
                <p className="text-sm text-green-600 dark:text-green-400">
                  {scannedBadge.replace('@+', '#')}
                </p>
              ) : scanStep === 'badge' ? (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {t('mobile.waiting') || 'Waiting...'}
                </p>
              ) : null}
            </div>
          </div>

          {/* Manual entry for badge */}
          {scanStep === 'badge' && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
              <div className="flex gap-2">
                <input
                  ref={badgeScanInputRef}
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualScanSubmit()}
                  placeholder="@+1234"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleManualScanSubmit}
                  disabled={!manualInput}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {t('common.go') || 'Go'}
                </button>
                <button
                  onClick={() => setShowCamera(true)}
                  className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  title={t('cameraScanner.openCamera') || 'Use Camera'}
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Label */}
        <div className={`p-4 md:p-5 rounded-xl border-2 transition-all ${
          scanStep === 'label'
            ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
            : 'border-gray-200 dark:border-gray-700'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center ${
              scanStep === 'label'
                ? 'bg-amber-100 dark:bg-amber-800 animate-pulse'
                : 'bg-gray-100 dark:bg-gray-700'
            }`}>
              {isLoading ? (
                <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
              ) : (
                <QrCode className={`w-6 h-6 ${scanStep === 'label' ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`} />
              )}
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-white">
                {t('mobile.step2ScanLabel') || '2. Scan Box Label'}
              </p>
              {scanStep === 'label' ? (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  {isLoading ? (t('mobile.verifying') || 'Verifying...') : (t('mobile.waiting') || 'Waiting...')}
                </p>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('mobile.scanBadgeFirst') || 'Scan badge first'}
                </p>
              )}
            </div>
          </div>

          {/* Manual entry for label */}
          {scanStep === 'label' && (
            <div className="mt-3 pt-3 border-t border-amber-200 dark:border-amber-800">
              <div className="flex gap-2">
                <input
                  ref={labelScanInputRef}
                  type="text"
                  value={manualInput}
                  onChange={(e) => setManualInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleManualScanSubmit()}
                  placeholder="#BOX-..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <button
                  onClick={handleManualScanSubmit}
                  disabled={!manualInput || isLoading}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {t('common.go') || 'Go'}
                </button>
                <button
                  onClick={() => setShowCamera(true)}
                  className="px-3 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  title={t('cameraScanner.openCamera') || 'Use Camera'}
                >
                  <Camera className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Cancel Button */}
      <button
        onClick={handleCancelConfirm}
        disabled={isLoading}
        className="w-full py-4 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold flex items-center justify-center gap-2"
      >
        <X className="w-5 h-5" />
        {t('common.cancel') || 'Cancel'}
      </button>
    </div>
  );
};

export default PackerScanConfirmation;
